/**
 * Remembers which sign-in destination a visitor picked on a company's landing
 * page, so a returning visitor can be sent straight there.
 *
 * Only links the admin marked with `data-remember-destination` in the landing
 * page HTML take part, and a stored choice is honoured only while a marked link
 * with the same URL is still on the page.
 */

export const REMEMBER_ATTRIBUTE = 'data-remember-destination';

export interface WelcomeDestination {
  href: string;
  label: string;
}

const storageKey = (subdomain: string) => `tio-welcome-destination:${subdomain || 'employee'}`;

const collapse = (text: string | null) => text?.replace(/\s+/g, ' ').trim() ?? '';

const isWebUrl = (href: string) => {
  try {
    const { protocol } = new URL(href);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * The marked destinations in a landing page's HTML, parsed inertly. A link's
 * non-blank `aria-label` names it in place of its visible text.
 *
 * @param html the admin-authored landing page HTML
 * @returns every marked `http(s)` link, in document order
 */
export function markedDestinations(html: string | undefined): WelcomeDestination[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll<HTMLAnchorElement>(`a[${REMEMBER_ATTRIBUTE}][href]`))
    .map((anchor) => ({
      href: anchor.getAttribute('href') ?? '',
      label: collapse(anchor.getAttribute('aria-label')) || collapse(anchor.textContent),
    }))
    .filter(({ href }) => isWebUrl(href));
}

/**
 * The destination this browser picked last time, if it is still offered.
 *
 * @param subdomain the company subdomain the choice was stored under
 * @param html the landing page HTML currently configured
 * @returns the stored destination, or undefined when none is stored, storage is
 *   unavailable, or the page no longer offers that link
 */
export function rememberedDestination(
  subdomain: string,
  html: string | undefined
): WelcomeDestination | undefined {
  let stored: string | null;
  try {
    stored = localStorage.getItem(storageKey(subdomain));
  } catch {
    return undefined;
  }
  if (!stored) return undefined;
  return markedDestinations(html).find(({ href }) => href === stored);
}

/**
 * Stores the destination a visitor picked. Silently does nothing when storage
 * is unavailable.
 *
 * @param subdomain the company subdomain to store the choice under
 * @param href the marked link's URL exactly as written in the HTML
 */
export function rememberDestination(subdomain: string, href: string): void {
  if (!isWebUrl(href)) return;
  try {
    localStorage.setItem(storageKey(subdomain), href);
  } catch {
    // Private mode or blocked storage: the visitor just picks again next time.
  }
}

/**
 * Forgets the stored destination for a company.
 *
 * @param subdomain the company subdomain the choice was stored under
 */
export function forgetDestination(subdomain: string): void {
  try {
    localStorage.removeItem(storageKey(subdomain));
  } catch {
    // Nothing stored that we could reach.
  }
}

const redirectedKey = (subdomain: string) => `tio-welcome-redirected:${subdomain || 'employee'}`;

/**
 * Records that this tab was already sent to the remembered destination, so
 * coming back to the landing page (Back from the identity provider, or after
 * signing out) shows the choices instead of redirecting again.
 *
 * @param subdomain the company subdomain the redirect was for
 */
export function markRedirected(subdomain: string): void {
  try {
    sessionStorage.setItem(redirectedKey(subdomain), '1');
  } catch {
    // Without session storage a return visit just redirects again.
  }
}

/**
 * Whether this tab was already sent to the remembered destination.
 *
 * @param subdomain the company subdomain to check
 */
export function redirectedThisTab(subdomain: string): boolean {
  try {
    return sessionStorage.getItem(redirectedKey(subdomain)) !== null;
  } catch {
    return false;
  }
}

/**
 * How long the landing page shows "Taking you to …" before following a
 * remembered destination, in milliseconds. Mutable so tests can shorten or
 * freeze the wait.
 */
export const welcomeRedirect = { delayMs: 5000 };
